import React, { useState } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Photo {
  src: string;
  alt: string;
  title: string;
  isLandscape: boolean;
}

const PhotoGallery: React.FC = () => {
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);

  // Church photos from assets folder
  const photos: Photo[] = [
    {
      src: require('../assets/altar+stained-glass-l.jpg'),
      alt: 'Altar with beautiful stained glass windows',
      title: 'Altar & Stained Glass',
      isLandscape: true
    },
    {
      src: require('../assets/altar-l.jpg'),
      alt: 'Main altar of All Saints Church',
      title: 'Main Altar',
      isLandscape: true
    },
    {
      src: require('../assets/altar.jpg'),
      alt: 'Close view of the altar',
      title: 'Altar Detail',
      isLandscape: false
    },
    {
      src: require('../assets/altar-close-l.jpg'),
      alt: 'Close-up view of the altar area',
      title: 'Altar Close-up',
      isLandscape: true
    },
    {
      src: require('../assets/bible-with-church-bg-l.jpg'),
      alt: 'Bible with church background',
      title: 'Sacred Scripture',
      isLandscape: true
    },
    {
      src: require('../assets/choir-loft-l.jpg'),
      alt: 'View of the choir loft',
      title: 'Choir Loft',
      isLandscape: true
    },
    {
      src: require('../assets/front-church.jpg'),
      alt: 'Front exterior of All Saints Church',
      title: 'Church Exterior',
      isLandscape: false
    },
    {
      src: require('../assets/holy-water.jpg'),
      alt: 'Holy water font',
      title: 'Holy Water Font',
      isLandscape: false
    },
    {
      src: require('../assets/pews-l.jpg'),
      alt: 'Church pews and interior',
      title: 'Church Interior',
      isLandscape: true
    },
    {
      src: require('../assets/stained-glass.jpg'),
      alt: 'Beautiful stained glass window',
      title: 'Stained Glass Window',
      isLandscape: false
    },
    {
      src: require('../assets/statue-christ.jpg'),
      alt: 'Statue of Christ',
      title: 'Christ Statue',
      isLandscape: false
    },
    {
      src: require('../assets/statue.jpg'),
      alt: 'Religious statue in the church',
      title: 'Sacred Statue',
      isLandscape: false
    },
    {
      src: require('../assets/view-from-choir-loft.jpg'),
      alt: 'View from the choir loft looking down at the nave',
      title: 'View from Choir Loft',
      isLandscape: false
    }
  ];

  const openLightbox = (index: number) => {
    setSelectedPhoto(index);
    document.body.style.overflow = 'hidden';
    // Focus the lightbox for keyboard navigation
    setTimeout(() => {
      const lightbox = document.querySelector('[role="dialog"]') as HTMLElement;
      if (lightbox) lightbox.focus();
    }, 0);
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
    document.body.style.overflow = 'unset';
  };

  const goToPrevious = () => {
    if (selectedPhoto !== null) {
      setSelectedPhoto((selectedPhoto - 1 + photos.length) % photos.length);
    }
  };

  const goToNext = () => {
    if (selectedPhoto !== null) {
      setSelectedPhoto((selectedPhoto + 1) % photos.length);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key >= '1' && e.key <= '9') {
      const index = parseInt(e.key) - 1;
      if (index < photos.length) {
        setSelectedPhoto(index);
      }
    }
  };

  return (
    <section className="py-16 bg-white" aria-labelledby="gallery-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 id="gallery-heading" className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Our Beautiful Church
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Take a virtual tour of All Saints Catholic Church and see the sacred spaces where our community gathers for worship and fellowship.
          </p>
          <p className="text-sm text-gray-500">
            Click any image to view in full size • Use arrow keys to navigate • Press Esc to close
          </p>
        </div>

        {/* Photo Grid - Masonry-style layout with consistent heights */}
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
          {photos.map((photo, index) => (
            <div
              key={index}
              className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] break-inside-avoid mb-4"
              onClick={() => openLightbox(index)}
            >
              <div className="relative">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-auto object-cover rounded-lg"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center rounded-lg">
                  <div className="text-white text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4">
                    <h3 className="text-lg font-semibold mb-2 drop-shadow-lg">{photo.title}</h3>
                    <p className="text-sm drop-shadow-lg">Click to view larger</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedPhoto !== null && (
          <div
            className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            role="dialog"
            autoFocus
            aria-modal="true"
            aria-labelledby="lightbox-title"
          >
            <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <img
                src={photos[selectedPhoto].src}
                alt={photos[selectedPhoto].alt}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
              
              {/* Close button */}
              <button
                onClick={closeLightbox}
                className="absolute top-6 right-6 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white rounded-full p-3 bg-black bg-opacity-50 hover:bg-opacity-70 transition-all"
                aria-label="Close gallery"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Navigation buttons */}
              <button
                onClick={goToPrevious}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white rounded-full p-3 bg-black bg-opacity-50 hover:bg-opacity-70 transition-all"
                aria-label="Previous photo"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>

              <button
                onClick={goToNext}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white rounded-full p-3 bg-black bg-opacity-50 hover:bg-opacity-70 transition-all"
                aria-label="Next photo"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>

              {/* Photo info */}
              <div className="absolute bottom-6 left-6 text-white bg-black bg-opacity-50 rounded-lg p-4">
                <h3 id="lightbox-title" className="text-xl font-semibold mb-1">
                  {photos[selectedPhoto].title}
                </h3>
                <p className="text-sm text-gray-300">
                  {selectedPhoto + 1} of {photos.length}
                </p>
              </div>

              {/* Thumbnail navigation for desktop */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 hidden md:flex space-x-2 bg-black bg-opacity-50 rounded-lg p-2">
                {photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedPhoto(index)}
                    className={`w-12 h-12 rounded overflow-hidden transition-all ${
                      index === selectedPhoto ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={photo.src}
                      alt={photo.title}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PhotoGallery;